import React, { useState } from "react";
import Button from "../../components/FormComponents/Button";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Input from "../../components/FormComponents/Input";
import Textarea from "../../components/FormComponents/Textarea";
import Label from "../../components/FormComponents/Label";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import Loader from "../../Layouts/Loader/Loader";
import useFetchData from "../../hooks/useFetchData";
import FileRepoTable from "./FileRepoTable";

const FileRepoRenderer = () => {
  const { data: allFiles, loading, error, setData: setAllFiles } = useFetchData("/file_repo");
  const { getUserConfirmationForDelete } = useModalContext();

  const handleUploadFile = async ({ file, name, description }) => {
    const formData = new FormData();
    formData.append("myFile", file);

    try {
      const uploadResponse = await axiosInstance.post(`/public/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const { name: fileName, original_name, type, _id, size } = uploadResponse.data;

      const payload = {
        file_id: _id,
        name: fileName,
        original_name,
        type,
        mime_type: type,
        size
      };

      const { data, message } = await axiosInstance.post(`/file_repo`, {
        file_data: payload,
        name,
        description
      });
      setAllFiles((prev) => [...prev, data]);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDeleteFileRepo = async (id) => {
    try {
      const confirm = await getUserConfirmationForDelete("file");
      if (!confirm) return;

      const { message } = await axiosInstance.delete(`file_repo/${id}`);
      setAllFiles((prev) => prev.filter((file) => file.id !== id));
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEditFileRepo = async (id, payload) => {
    try {
      const { message } = await axiosInstance.put(`file_repo/${id}`, payload);

      setAllFiles((pre) => {
        return pre.map((file) => {
          if (file.id === id) {
            return { ...file, ...payload };
          } else {
            return file;
          }
        });
      });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  if (loading) return <Loader />;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="w_100 p_3">
      <CreateFileRepo onCreate={handleUploadFile} />
      <FileRepoTable files={allFiles} onDelete={handleDeleteFileRepo} onEdit={handleEditFileRepo} />
    </div>
  );
};

const initialCreateModalState = {
  show: false,
  title: "",
  type: ""
};
const CreateFileRepo = ({ onCreate }) => {
  const [modalState, setModalState] = useState(initialCreateModalState);
  const openModal = (type, title) => {
    setModalState({
      show: true,
      title,
      type
    });
  };

  const closeModal = () => {
    setModalState(initialCreateModalState);
  };

  const handleCreateFileRepo = async (data) => {
    await onCreate(data);
    closeModal();
  };

  return (
    <>
      <Button onClick={() => openModal("add", "Add File")}>+ Add File</Button>
      <BuilderModal isOpen={modalState.show} onClose={closeModal} modalName={modalState.title}>
        <FileUploadForm onUploadFile={handleCreateFileRepo} />
      </BuilderModal>
    </>
  );
};

const FileUploadForm = ({ onUploadFile }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!file || !name.trim()) return;

    setLoading(true);
    setError("");

    try {
      await onUploadFile({
        file,
        name: name.trim(),
        description: description.trim()
      });
      setFile(null);
      setName("");
      setDescription("");
    } catch (err) {
      setError(err?.message || "Something went wrong during file upload.");
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = !file || !name.trim() || loading;

  return (
    <div style={{ maxWidth: "400px", display: "flex", flexDirection: "column", gap: "10px" }}>
      <FileUpload onUpload={setFile} accept=".pdf,.ppt,.pptx,.doc,.docx" disabled={loading} />

      <Input
        placeholder="File Display Name *"
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={loading}
      />

      <Textarea
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        label="Description"
        disabled={loading}
      />

      {error && <div style={{ color: "red", fontSize: "0.9rem" }}>{error}</div>}

      <Button onClick={handleSubmit} disabled={isDisabled}>
        {loading ? "Uploading..." : "Upload File"}
      </Button>
    </div>
  );
};

const FileUpload = ({ onUpload, accept = "*", multiple = false, label = "Upload File", className = "" }) => {
  const handleChange = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    onUpload(multiple ? Array.from(files) : files[0]);
  };

  return (
    <div className={`${className}`}>
      <Label>
        {label}
        <Input type="file" onChange={handleChange} accept={accept} multiple={multiple} className="hidden" />
      </Label>
    </div>
  );
};

export default FileRepoRenderer;
